import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiArrowLeft } = FiIcons;

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
            <span>Back to Status Checker</span>
          </Link>
          
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiShield} className="h-8 w-8 text-green-500" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Privacy Policy
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md space-y-6"
        >
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Our Privacy Commitment
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Web Down Status is built with privacy as a core principle. We believe that website monitoring 
              should not come at the cost of your privacy. This policy explains how we handle (or rather, 
              don't handle) your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              What We Don't Collect
            </h2>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Personal Information:</strong> We don't collect names, email addresses, or any personal identifiers.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Website Check History:</strong> Your check history is stored locally in your browser only.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Tracking Data:</strong> No analytics, tracking pixels, or behavioral data collection.
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Cookies:</strong> We don't use tracking cookies or store unnecessary data.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              How the Service Works
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              When you check a website status:
            </p>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Your browser sends the URL to our server or proxy for checking
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Our server performs the status check and returns the results
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  The URL and results are immediately discarded from our servers
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Results are stored only in your browser's local storage (if you choose)
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Local Storage
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              The only data stored is in your browser's local storage, which includes:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Your check history (last 500 checks)</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Your preferred settings (theme, timeouts, etc.)</span>
              </li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mt-3">
              This data never leaves your device and can be cleared at any time through your browser settings 
              or using our "Clear History" function.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Third-Party Services
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              We use minimal third-party services to provide our functionality:
            </p>
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Cloudflare Workers</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Used as a proxy service for CORS-blocked requests. Cloudflare may log basic request 
                  information according to their privacy policy.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Cloudflare DNS over HTTPS</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Used for DNS resolution when enabled. DNS queries are sent to Cloudflare's public DNS service.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Security
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Since we don't collect or store personal data, there's minimal security risk. However, 
              we implement standard security measures including:
            </p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">HTTPS encryption for all communications</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Rate limiting to prevent abuse</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <span className="text-gray-600 dark:text-gray-300">Input validation and sanitization</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Changes to This Policy
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              If we ever change this privacy policy, we will update the "Last updated" date at the top 
              of this page. Given our commitment to privacy, any changes will only strengthen our privacy 
              protections, never weaken them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Contact
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              If you have any questions about this privacy policy or our practices, please contact us at:
              <br />
              <a href="mailto:privacy@webdownstatus.com" className="text-primary-600 dark:text-primary-400 hover:underline">
                privacy@webdownstatus.com
              </a>
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;